import _cloneDeep from 'lodash.clonedeep'
import _isEqual from 'lodash.isequal'
import { formatDate, formatTime } from '@smarttransit/common'
import {
  addOfflineNotificationSetting,
  deleteOfflineNotificationSetting,
  getOfflineNotificationSetting,
  getOfflineNotificationSettings,
  updateOfflineNotificationSetting
} from '@/services/st-settings-service'
import { addAlert } from '../../../../utilities/helpers'

const defaultProtectedAgencyId = 'STRANSITGHA'
const defaultCurrentConfig = {
  agencyId: '',
  enabled: true,
  offlineThresholdMinutes: 15,
  notificationIntervalMinutes: 30,
  consecutiveMapApiFailures: 4,
  contacts: []
}

export default {
  name: 'advanced-check-offline-buses',
  props: {
    signedInUser: Object
  },
  data () {
    return {
      configs: [],
      apiInProgress: false,
      currentConfig: null,
      originalCurrentConfig: null,
      defaultProtectedAgencyId,
      contactTypes: [
        { text: 'Phone', value: 'phone' },
        { text: 'Email', value: 'email' }
      ]
    }
  },
  computed: {
    isCurrentConfigEdited () {
      return this.currentConfig && !_isEqual(this.originalCurrentConfig, this.currentConfig)
    },
    agencyOptions () {
      const agencies = this.$store.getters.getAgencies || []
      return agencies.map((agency) => (agency.value))
    }
  },
  mounted () {
    this.loadConfigs()
  },
  methods: {
    getAgencyLabel (agencyId) {
      const agencies = this.$store.getters.getAgencies || []
      const found = agencies.find((agency) => (agency.value === agencyId))
      return found ? `${found.text} (${agencyId})` : agencyId
    },
    normalizeConfig (config) {
      return {
        id: config.id,
        agencyId: config.metadata,
        enabled: config.data && typeof config.data.enabled === 'boolean' ? config.data.enabled : true,
        offlineThresholdMinutes: config.data && config.data.offlineThresholdMinutes ? Number(config.data.offlineThresholdMinutes) : 10,
        notificationIntervalMinutes: config.data && config.data.notificationIntervalMinutes ? Number(config.data.notificationIntervalMinutes) : 30,
        consecutiveMapApiFailures: config.data && !isNaN(Number(config.data.consecutiveMapApiFailures)) ? Number(config.data.consecutiveMapApiFailures) : 0,
        contacts: config.data && Array.isArray(config.data.contacts) ? _cloneDeep(config.data.contacts) : [],
        dateUpdated: config.dateUpdated
      }
    },
    async loadConfigs () {
      try {
        const configs = await getOfflineNotificationSettings()
        this.configs = (configs || []).map((config) => {
          const normalized = this.normalizeConfig(config)
          normalized.formattedDateUpdated = normalized.dateUpdated ? `${formatDate(normalized.dateUpdated)}, ${formatTime(normalized.dateUpdated)}` : ''
          return normalized
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      }
    },
    async editConfig (id) {
      try {
        const config = await getOfflineNotificationSetting(id)
        this.currentConfig = this.normalizeConfig(config)
        this.originalCurrentConfig = _cloneDeep(this.currentConfig)
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      }
    },
    createConfig () {
      if (this.isCurrentConfigEdited && !confirm('Confirm overwriting the current config')) {
        return
      }
      this.currentConfig = _cloneDeep(defaultCurrentConfig)
      this.originalCurrentConfig = _cloneDeep(this.currentConfig)
    },
    addContact () {
      if (!this.currentConfig) {
        return
      }
      this.currentConfig.contacts.push({
        type: 'phone',
        name: '',
        value: ''
      })
    },
    removeContact (index) {
      if (!this.currentConfig || !this.currentConfig.contacts) {
        return
      }
      this.currentConfig.contacts.splice(index, 1)
    },
    getCompleteContacts (contacts) {
      return (Array.isArray(contacts) ? contacts : [])
        .map((contact) => ({
          type: contact && contact.type ? String(contact.type).trim() : '',
          name: contact && contact.name ? String(contact.name).trim() : '',
          value: contact && contact.value ? String(contact.value).trim() : ''
        }))
        .filter((contact) => (contact.type && contact.name && contact.value))
    },
    validateConfig () {
      if (!this.currentConfig || !this.currentConfig.agencyId) {
        alert('Please provide an agency ID')
        return false
      }
      if (!Number(this.currentConfig.offlineThresholdMinutes) || Number(this.currentConfig.offlineThresholdMinutes) <= 0) {
        alert('Offline threshold minutes must be a positive number')
        return false
      }
      if (!Number(this.currentConfig.notificationIntervalMinutes) || Number(this.currentConfig.notificationIntervalMinutes) <= 0) {
        alert('Notification interval minutes must be a positive number')
        return false
      }
      if (isNaN(Number(this.currentConfig.consecutiveMapApiFailures)) || Number(this.currentConfig.consecutiveMapApiFailures) < 0) {
        alert('Consecutive map-api failures must be 0 or greater')
        return false
      }
      const completeContacts = this.getCompleteContacts(this.currentConfig.contacts)
      if (!completeContacts.length) {
        alert('Please add at least one complete contact with type, name and value')
        return false
      }

      return true
    },
    async saveConfig () {
      if (!this.validateConfig()) {
        return
      }

      this.apiInProgress = true

      try {
        const contacts = this.getCompleteContacts(this.currentConfig.contacts)

        if (this.currentConfig.id) {
          await updateOfflineNotificationSetting({
            id: this.currentConfig.id,
            contacts,
            offlineThresholdMinutes: Number(this.currentConfig.offlineThresholdMinutes),
            notificationIntervalMinutes: Number(this.currentConfig.notificationIntervalMinutes),
            enabled: Boolean(this.currentConfig.enabled),
            consecutiveMapApiFailures: Number(this.currentConfig.consecutiveMapApiFailures)
          })

          addAlert({
            message: `Successfully updated config for agency: ${this.currentConfig.agencyId}`,
            type: 'success',
            transient: true
          })

          await this.loadConfigs()
          await this.editConfig(this.currentConfig.id)
        } else {
          await addOfflineNotificationSetting({
            agencyId: this.currentConfig.agencyId,
            contacts,
            offlineThresholdMinutes: Number(this.currentConfig.offlineThresholdMinutes),
            notificationIntervalMinutes: Number(this.currentConfig.notificationIntervalMinutes),
            enabled: Boolean(this.currentConfig.enabled),
            consecutiveMapApiFailures: Number(this.currentConfig.consecutiveMapApiFailures)
          })
          addAlert({
            message: `Successfully created config for agency: ${this.currentConfig.agencyId}`,
            type: 'success'
          })
          await this.loadConfigs()
          this.currentConfig = null
          this.originalCurrentConfig = null
        }
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    async deleteConfig (config) {
      if (!config || !config.id) {
        return
      }

      if (config.agencyId === defaultProtectedAgencyId) {
        return alert(`Cannot delete default agency config (${defaultProtectedAgencyId})`)
      }

      if (this.currentConfig && this.currentConfig.id === config.id) {
        return alert('Cannot delete a config that is currently being edited')
      }

      if (!confirm(`Confirm deleting config for agency: ${config.agencyId}?`)) {
        return
      }

      this.apiInProgress = true

      try {
        await deleteOfflineNotificationSetting(config.id)
        await this.loadConfigs()

        addAlert({
          message: `Successfully deleted config for agency: ${config.agencyId}`,
          type: 'success',
          transient: true
        })
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.apiInProgress = false
      }
    },
    cancelConfigEdit () {
      if (this.isCurrentConfigEdited && !confirm('Confirm closing this edit, any changes will be lost')) {
        return
      }

      this.currentConfig = null
      this.originalCurrentConfig = null
    }
  }
}
